import router from "@/router";
import { AdminRoutes, ClientRoutes } from "@/data/enums/router";
import type { ApiPathGetter } from "@upmind-automation/types";
import type { IEmail } from "@upmind-automation/types";
import type { IState } from "@/store";
import type { IDataState } from "@/store/modules/data";
import { DataModules } from "@/store/modules/data/modules";
import { Methods } from "@upmind-automation/types";
import type { ActionTree, GetterTree } from "vuex";

const initialState = {} as IDataState;

const getters: GetterTree<IDataState, IState> = {
  apiPath:
    (s, g, rS, { isAdminContext, isMockClientContext }): ApiPathGetter =>
    hasToken => {
      const admin = `api/admin/notifications/email-topic-opt-ins`;
      const client = `api/notifications/email-topic-opt-ins`;
      const contextual =
        isAdminContext && !isMockClientContext() && !hasToken ? admin : client;
      return { client, admin, contextual };
    },
  scope: () => {
    return "$email_topic_opt_ins";
  }
};

const actions: ActionTree<IDataState, IState> = {
  /**
   * List the current topic opt-ins for an address.
   * - Account holder: `GET .../{clientEmailId}`.
   * - Guest (token): `GET ...?token={token}` — the token binds the address.
   */
  list: ({ dispatch, getters }, payload) => {
    const token = payload?.params?.token;
    return dispatch(
      "data/list",
      {
        ...payload,
        path: `${getters.apiPath(!!token).contextual}${
          payload.clientEmailId ? `/${payload.clientEmailId}` : ``
        }`,
        handle401Error: !token,
        storeModule: DataModules.NOTIFICATIONS_EMAIL_OPT_OUTS
      },
      { root: true }
    );
  },
  /**
   * Sync (account holder only) — send the full desired set of topic opt-ins.
   * `PUT .../{clientEmailId}` with `{ topics: [{ id }, …] }`. Diff-applied
   * server-side; an empty array clears all opt-ins for the address.
   */
  sync: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/update",
      {
        ...payload,
        path: `${getters.apiPath().contextual}/${payload.clientEmailId}`,
        storeModule: DataModules.NOTIFICATIONS_EMAIL_OPT_OUTS,
        data: payload.data
      },
      { root: true }
    );
  },
  /**
   * Unsubscribe (guest only) — remove one or more topic opt-ins for the
   * token-bound address. `DELETE ...?token={token}&topic_ids[]=…`.
   * `topic_ids` must be non-empty (server returns 422 otherwise).
   */
  unsubscribe: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/callApi",
      {
        method: Methods.DELETE,
        path: getters.apiPath(true).contextual,
        requestConfig: {
          params: {
            token: payload.token,
            topic_ids: payload.topic_ids
          },
          handle401Error: false
        },
        vm: payload.vm
      },
      { root: true }
    );
  },
  /**
   * Open the address's notification management. The default address never has
   * per-address opt-ins (it always receives, governed by the account-level
   * notification preferences), so it is routed to the notifications page
   * instead of the per-address modal.
   */
  openManageOptIns: ({ dispatch, rootGetters }, email: IEmail) => {
    if (email.default) {
      const isAdmin =
        rootGetters.isAdminContext && !rootGetters.isMockClientContext();
      router
        .push(
          isAdmin
            ? {
                name: AdminRoutes.CLIENT_NOTIFICATIONS,
                params: { clientId: email.client_id }
              }
            : { name: ClientRoutes.NOTIFICATIONS }
        )
        // Ignore redundant-navigation errors (already on the page).
        .catch(() => undefined);
      return;
    }

    dispatch(
      "ui/open/windowModal",
      {
        config: {
          component: () =>
            import(
              "@/components/app/global/client/manageEmailTopicOptInsModal.vue"
            ),
          props: { email },
          width: 560
        }
      },
      { root: true }
    );
  }
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions
};
